import React from 'react';
import { Loan, Queue } from '../types';
import { X, ExternalLink, Activity, User, AlertTriangle, MousePointerClick } from 'lucide-react';
interface ContextPanelProps {
  loan: Loan | null;
  queue: Queue;
  onClose: () => void;
  onClaimLoan?: (loanId: string) => void;
  onRequestTakeOver?: (loanId: string) => void;
}
export function ContextPanel({
  loan,
  queue,
  onClose,
  onClaimLoan,
  onRequestTakeOver
}: ContextPanelProps) {
  if (!loan) {
    return <div className="w-[360px] border-l border-slate-200 bg-slate-50/50 p-8 flex flex-col items-center justify-center shrink-0 text-center" data-id="element-97">
        <div className="w-16 h-16 bg-white rounded-full border border-slate-200 flex items-center justify-center mb-4 shadow-sm" data-id="element-98">
          <MousePointerClick size={24} className="text-blue-500" data-id="element-99" />
        </div>
        <h2 className="text-base font-semibold text-slate-900 mb-1" data-id="element-100">
          Select a loan
        </h2>
        <p className="text-sm text-slate-500 leading-relaxed max-w-[240px]" data-id="element-101">
          Click on any loan in the queue to view its details, claim it, or take
          it over.
        </p>
      </div>;
  }
  return <div className="w-[360px] border-l border-slate-200 bg-white flex flex-col shrink-0" data-id="element-102">
      <div className="p-5 border-b border-slate-200 flex items-start justify-between bg-slate-50/50" data-id="element-103">
        <div data-id="element-104">
          <div className="flex items-center gap-2 mb-1" data-id="element-105">
            <h2 className="text-xl font-semibold text-slate-900 tracking-tight" data-id="element-106">
              {loan.name}
            </h2>
            <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors" data-id="element-107">
              <ExternalLink size={16} data-id="element-108" />
            </a>
          </div>
          <p className="text-base text-slate-600" data-id="element-109">{loan.borrowerName}</p>
        </div>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-full transition-colors" data-id="element-110">
          <X size={18} data-id="element-111" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-112">
        {/* Primary Actions */}
        <div className="space-y-3" data-id="element-113">
          <button disabled={loan.claimState !== 'Unclaimed' || queue.isOpportunityOnly} onClick={() => onClaimLoan?.(loan.id)} title={queue.isOpportunityOnly ? 'Opportunity-only update — does not create a Loan_Claim__c record' : loan.claimState !== 'Unclaimed' ? 'Already claimed' : 'Claim this loan into My Claimed Loans'} className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600" data-id="element-114">
            {queue.isOpportunityOnly ? <AlertTriangle size={16} data-id="element-115" /> : <Activity size={16} data-id="element-116" />}
            {queue.isOpportunityOnly ? 'Alert Claim' : 'Claim this loan'}
          </button>

          <button disabled={loan.claimState === 'Unclaimed'} onClick={() => onRequestTakeOver?.(loan.id)} title={loan.claimState === 'Unclaimed' ? 'Loan must be claimed before it can be taken over' : 'Take Over'} className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white" data-id="element-117">
            <User size={16} data-id="element-118" />
            Take over this loan
          </button>

          {queue.isOpportunityOnly && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 mt-2" data-id="element-119">
              <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" data-id="element-120" />
              <p className="text-xs text-amber-800 leading-relaxed" data-id="element-121">
                This queue uses Opportunity-only field updates. Actions here may
                not create a standard{' '}
                <code className="bg-amber-100 px-1 rounded" data-id="element-122">Loan_Claim__c</code>{' '}
                record.
              </p>
            </div>}
        </div>

        {/* Details Grid */}
        <div data-id="element-123">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-124">
            Loan Details
          </h3>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 grid grid-cols-2 gap-y-5 gap-x-4" data-id="element-125">
            <div data-id="element-126">
              <div className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide" data-id="element-127">
                Amount
              </div>
              <div className="text-sm font-semibold text-slate-900" data-id="element-128">
                ${loan.amount.toLocaleString()}
              </div>
            </div>
            <div data-id="element-129">
              <div className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide" data-id="element-130">
                Rate
              </div>
              <div className="text-sm font-semibold text-slate-900" data-id="element-131">
                {loan.rate}%
              </div>
            </div>
            <div data-id="element-132">
              <div className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide" data-id="element-133">
                Type
              </div>
              <div className="text-sm font-semibold text-slate-900" data-id="element-134">
                {loan.type}
              </div>
            </div>
            <div data-id="element-135">
              <div className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide" data-id="element-136">
                Purpose
              </div>
              <div className="text-sm font-semibold text-slate-900" data-id="element-137">
                {loan.purpose}
              </div>
            </div>
            <div className="col-span-2" data-id="element-138">
              <div className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide" data-id="element-139">
                Lender
              </div>
              <div className="text-sm font-semibold text-slate-900" data-id="element-140">
                {loan.lender}
              </div>
            </div>
            <div className="col-span-2" data-id="element-141">
              <div className="text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wide" data-id="element-142">
                Loan Partner
              </div>
              <div className="text-sm font-semibold text-slate-900" data-id="element-143">
                {loan.partner}
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div data-id="element-144">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-145">
            Claim History
          </h3>
          <div className="relative pl-4 border-l-2 border-slate-200 space-y-5 ml-2" data-id="element-146">
            <div className="relative" data-id="element-147">
              <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-blue-500 ring-4 ring-white" data-id="element-148" />
              <p className="text-sm font-semibold text-slate-900" data-id="element-149">
                Entered Queue
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-150">
                {loan.relevantDate} • {loan.relevantDateLabel}
              </p>
            </div>
            {loan.claimState !== 'Unclaimed' && <div className="relative" data-id="element-151">
                <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-white" data-id="element-152" />
                <p className="text-sm font-semibold text-slate-900" data-id="element-153">
                  {loan.claimState}
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-154">
                  {loan.lastUpdate} • System
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}